import { Box, Link, Typography, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { clearStorage } from "utils/clear-storage.util";
import { logClientEvent } from "utils/client-log.util";

// If the splash is still up after this long, reload the page once on its own.
// If it's still stuck after that reload, show the log out message instead.
const STUCK_MS = 30000;

// sessionStorage survives a reload but is per-tab, so "once" means once per
// stuck attempt in this tab. Cleared when the splash goes away normally.
const AUTO_RELOADED_KEY = "splashAutoReloaded";

const SplashScreen = () => {
  const theme = useTheme();
  const [stuck, setStuck] = useState(false);
  const subscriptionError = useSelector(
    (state) => state.account.subscriptionError
  );

  useEffect(() => {
    const timer = setTimeout(() => setStuck(true), STUCK_MS);
    return () => {
      clearTimeout(timer);
      // Splash unmounted without a page reload: the app loaded, so a later
      // stuck splash gets its own auto reload.
      sessionStorage.removeItem(AUTO_RELOADED_KEY);
    };
  }, []);

  const alreadyReloaded = sessionStorage.getItem(AUTO_RELOADED_KEY) === "1";
  const failed = stuck || subscriptionError;
  const showMessage = failed && alreadyReloaded;

  useEffect(() => {
    if (!failed || alreadyReloaded) return;
    sessionStorage.setItem(AUTO_RELOADED_KEY, "1");
    logClientEvent({
      source: "App/Startup/SplashScreenAutoReload",
      level: "Warning",
      message: subscriptionError
        ? "Account details failed to load; auto reloading once"
        : `SplashScreen still showing after ${STUCK_MS}ms; auto reloading once`,
    });
    window.location.reload();
  }, [failed, alreadyReloaded, subscriptionError]);

  useEffect(() => {
    if (!showMessage) return;
    logClientEvent({
      source: "App/Startup/SplashScreenLoginErrorShown",
      level: "Warning",
      message: "SplashScreen still stuck after auto reload; showing log out message",
      extra: { subscriptionError },
    });
  }, [showMessage, subscriptionError]);

  const handleLogout = async (e) => {
    e.preventDefault();
    // Log first, while the user is still in storage so the log has their email.
    // Don't let a slow network hold up the logout itself.
    await Promise.race([
      logClientEvent({
        source: "App/Startup/SplashScreenLogoutClicked",
        level: "Information",
        message: "User clicked Log out on splash screen login error",
      }),
      new Promise((resolve) => setTimeout(resolve, 2000)),
    ]);
    sessionStorage.removeItem(AUTO_RELOADED_KEY);
    clearStorage();
    localStorage.removeItem("login");
    window.location.replace("/");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        bgcolor: theme.palette.secondary.main,
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      }}
    >
      <Box
        component="img"
        width="200px"
        maxHeight="100%"
        src={theme.palette.mode === "dark" ? "/images/sooth_horizontal_on_dark.png" : "/images/sooth_horizontal_on_white.png"}
        alt="Sooth.fyi Logo"
      />
      {showMessage && (
        <Typography variant="body2" color="text.secondary" textAlign="center" px={2}>
          Something went wrong while logging you in, please{" "}
          <Link href="#" onClick={handleLogout}>
            Log out
          </Link>{" "}
          and sign in again.
        </Typography>
      )}
    </Box>
  );
};

export default SplashScreen;
